import React, { useState, useMemo } from "react";
import useSWR from "swr";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_LABEL = {
  roofing_system: { en: "Roofing Systems", fr: "Systèmes de toiture" },
  siding_system: { en: "Siding Systems", fr: "Systèmes de revêtement" },
  soffit_fascia: { en: "Soffit & Fascia", fr: "Soffite & Fascia" },
  roof_flashing: { en: "Roof Flashing", fr: "Solins toiture" },
  siding_trim: { en: "Siding Trim", fr: "Moulures revêtement" },
  underlayment: { en: "Underlayment", fr: "Sous-couches" },
  deck_repair: { en: "Deck Repair", fr: "Réparation pontage" },
  fasteners: { en: "Fasteners", fr: "Attaches" },
  sealants: { en: "Sealants", fr: "Scellants" },
  ventilation: { en: "Ventilation", fr: "Ventilation" },
  gutters: { en: "Gutters", fr: "Gouttières" },
  snow_ice: { en: "Snow & Ice", fr: "Neige & Glace" },
  penetrations: { en: "Skylights / Pipes", fr: "Puits de lumière / Évents" },
  surcharges: { en: "Surcharges", fr: "Suppléments" },
  safety_equipment: { en: "Safety / Setup", fr: "Sécurité / Installation" },
  service: { en: "Service / Labor", fr: "Service / Main-d'œuvre" },
  custom_metal: { en: "Custom Metal", fr: "Métal sur mesure" },
  labor: { en: "Labor", fr: "Main-d'œuvre" },
  other: { en: "Other", fr: "Autre" },
};

export default function Estimator() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const { data: projects = [] } = useSWR("/projects", fetcher);
  const { data: catalog = [] } = useSWR("/catalog", fetcher);
  const [projectId, setProjectId] = useState(location.state?.project_id || "");
  const [lines, setLines] = useState([]);
  const [markup, setMarkup] = useState(20);
  const [tax, setTax] = useState(14.975);

  const updateLine = (i, patch) => {
    const next = [...lines];
    next[i] = { ...next[i], ...patch };
    next[i].total = Math.round(Number(next[i].qty || 0) * Number(next[i].unit_price || 0) * 100) / 100;
    setLines(next);
  };

  const addFromCatalog = (itemId) => {
    const c = catalog.find((x) => x.id === itemId);
    if (!c) return;
    setLines([...lines, {
      label: (lang === "fr" && c.label_fr) ? c.label_fr : c.label,
      category: c.category,
      qty: 1,
      unit: c.unit,
      unit_price: c.unit_price,
      total: c.unit_price,
    }]);
  };

  const subtotal = useMemo(() => lines.reduce((a, b) => a + Number(b.total || 0), 0), [lines]);
  const marked = subtotal * (1 + markup / 100);
  const total = marked * (1 + tax / 100);

  const save = async () => {
    if (!projectId) return toast.error(lang === "fr" ? "Sélectionnez un projet" : "Select a project");
    const cleaned = lines.filter((l) => l.label && Number(l.qty) > 0);
    if (cleaned.length === 0) return toast.error(lang === "fr" ? "Ajoutez au moins une ligne" : "Add at least one line");
    try {
      await api.post("/estimates", { project_id: projectId, lines: cleaned, markup_pct: Number(markup), tax_pct: Number(tax) });
      toast.success(lang === "fr" ? "Estimation enregistrée" : "Estimate saved");
    } catch { toast.error("Save failed"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    catalog.filter((c) => c.active).forEach((c) => { (g[c.category] ||= []).push(c); });
    return g;
  }, [catalog]);

  const visibleCategories = useMemo(
    () => Object.keys(CATEGORY_LABEL).filter((k) => grouped[k]),
    [grouped]
  );

  return (
    <div data-testid="estimator-page">
      <PageHeader
        eyebrow="// 04 / Estimator"
        title={t("estimator.title")}
        sub={t("estimator.sub")}
        action={
          <Button data-testid="estimate-save" onClick={save} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
            <FloppyDisk size={14} className="mr-2" /> {lang === "fr" ? "Enregistrer" : "Save Estimate"}
          </Button>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-zinc-200">
            <div className="border-b border-zinc-200 p-4 flex items-center gap-3 flex-wrap">
              <Label className="label-eyebrow text-zinc-600">{lang === "fr" ? "Projet" : "Project"}</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="estimate-project-select" className="rounded-none w-72"><SelectValue placeholder={lang === "fr" ? "Sélectionner…" : "Select project…"} /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex-1" />
              <Select onValueChange={addFromCatalog}>
                <SelectTrigger data-testid="estimate-catalog-select" className="rounded-none w-80">
                  <SelectValue placeholder={lang === "fr" ? "+ Depuis le catalogue C.H.C." : "+ From C.H.C. catalog"} />
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  {visibleCategories.map((k) => (
                    <SelectGroup key={k}>
                      <SelectLabel className="label-eyebrow text-orange-600">{CATEGORY_LABEL[k][lang === "fr" ? "fr" : "en"]}</SelectLabel>
                      {grouped[k].map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {(lang === "fr" && c.label_fr) ? c.label_fr : c.label}
                          <span className="text-zinc-400 ml-2 mono text-xs">· {fmt$(c.unit_price)}/{c.unit}</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-12 px-4 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-5">{lang === "fr" ? "Article" : "Item"}</div>
              <div className="col-span-1">Qty</div>
              <div className="col-span-1">Unit</div>
              <div className="col-span-2">Unit $</div>
              <div className="col-span-3 text-right">{lang === "fr" ? "Total" : "Total"}</div>
            </div>
            {lines.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-zinc-500">
                {lang === "fr" ? "Sélectionnez un projet puis ajoutez des articles depuis votre catalogue C.H.C." : "Pick a project, then add items from your C.H.C. catalog."}
              </div>
            ) : lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-100 items-center">
                <Input data-testid={`est-label-${i}`} className="col-span-5 rounded-none" value={l.label} onChange={(e) => updateLine(i, { label: e.target.value })} />
                <Input data-testid={`est-qty-${i}`} type="number" step="0.01" className="col-span-1 rounded-none mono" value={l.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} />
                <Input className="col-span-1 rounded-none mono text-xs" value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })} />
                <Input data-testid={`est-price-${i}`} type="number" step="0.01" className="col-span-2 rounded-none mono" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                <div className="col-span-3 flex items-center justify-end gap-2 mono text-sm">
                  {fmt$(l.total)}
                  <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="text-zinc-400 hover:text-orange-600">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
            <div className="p-4">
              <Button data-testid="est-add-line" variant="outline" onClick={() => setLines([...lines, { label: "", category: "other", qty: 1, unit: "ea", unit_price: 0, total: 0 }])}
                className="rounded-none border-zinc-300 text-xs uppercase font-bold tracking-wider">
                <Plus size={14} className="mr-2" /> {lang === "fr" ? "Ligne vide" : "Blank line"}
              </Button>
            </div>
          </div>

          <aside className="border border-zinc-200 h-fit">
            <div className="border-b border-zinc-200 px-5 py-3">
              <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Totaux" : "Totals"}</p>
            </div>
            <div className="p-5 space-y-4">
              <SumRow k={lang === "fr" ? "SOUS-TOTAL" : "SUBTOTAL"} v={fmt$(subtotal)} />
              <div className="flex items-center justify-between gap-3">
                <span className="label-eyebrow text-zinc-500">MARKUP %</span>
                <Input data-testid="est-markup" type="number" value={markup} onChange={(e) => setMarkup(Number(e.target.value))} className="rounded-none w-24 mono text-right" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="label-eyebrow text-zinc-500">TPS+TVQ %</span>
                <Input data-testid="est-tax" type="number" step="0.001" value={tax} onChange={(e) => setTax(Number(e.target.value))} className="rounded-none w-24 mono text-right" />
              </div>
              <SumRow k={lang === "fr" ? "APRÈS MARGE" : "AFTER MARKUP"} v={fmt$(marked)} />
              <div className="border-t border-zinc-300 pt-4">
                <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "TOTAL CLIENT" : "CUSTOMER TOTAL"}</p>
                <p data-testid="estimate-total" className="font-display font-black text-3xl mt-2 text-orange-600 tracking-tighter">{fmt$(total)}</p>
              </div>
            </div>
          </aside>
        </div>
      </PageBody>
    </div>
  );
}

function SumRow({ k, v }) {
  return (
    <div className="flex justify-between items-baseline border-b border-zinc-100 pb-2 last:border-b-0">
      <span className="label-eyebrow text-zinc-500">{k}</span>
      <span className="mono text-sm text-zinc-900">{v}</span>
    </div>
  );
}
